import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Avatar,
    Card,
    TextField,
    IconButton,
    CircularProgress,
    Divider,
    Paper,
    Tooltip
} from '@mui/material';
import { ArrowBack, Send, RadioButtonUnchecked, CheckCircle } from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/pt';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import { sendMessage } from '../../../services/chat';
import { useMarkAsSeen, useMessages } from '../../../hooks/useChat';
import { getPanelRole } from '../../../services/api';

const MessageChat = () => {
    const { pacienteId } = useParams();
    const { i18n } = useTranslation();

    const navigate = useNavigate();
    const { data: messages, isLoading } = useMessages(pacienteId, true, 5000);
    const markAsSeen = useMarkAsSeen();
    const [newMessage, setNewMessage] = useState('');
    const bottomRef = useRef(null);
    const queryClient = useQueryClient();

    const roleUUID = getPanelRole();

    useEffect(() => {
        dayjs.locale(i18n.language);
    }, [i18n.language]);

    const patientName =
        messages?.[0]?.sender?.uuid !== roleUUID
            ? messages[0]?.sender?.name
            : messages?.[0]?.receiver?.name;

    const formatMessageDate = (dateString) => {
        const d = dayjs(dateString);
        const now = dayjs();

        if (d.isSame(now, 'day')) {
            return `${t('messages.today')} ${d.format('HH:mm')}`;
        }

        if (d.isSame(now.subtract(1, 'day'), 'day')) {
            return `${t('messages.yesterday')} ${d.format('HH:mm')}`;
        }

        if (d.isAfter(now.subtract(7, 'day'))) {
            return `${d.format('dddd')} ${d.format('HH:mm')}`;
        }

        return d.format('DD/MM HH:mm');
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (bottomRef.current) {
                bottomRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        }, 50);

        return () => clearTimeout(timeout);
    }, [messages]);

    useEffect(() => {
        if (!messages || !messages.length) return;

        const unseenMessages = messages
            .filter((msg) => msg.receiver?.uuid === roleUUID && !msg.seen)
            .map((msg) => msg.uuid);

        if (unseenMessages.length > 0) {
            markAsSeen.mutate(unseenMessages);
        }
    }, [messages]);

    const handleSend = async () => {
        if (!newMessage.trim()) return;

        try {
            await sendMessage({ to: pacienteId, message: newMessage });
            setNewMessage('');
            queryClient.invalidateQueries(['messages', { chatterUuid: pacienteId }]);
        } catch (err) {
            console.error('Erro ao enviar mensagem:', err);
        }
    };

    const handleBack = () => navigate('/mensagens');

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
                <CircularProgress sx={{ color: '#1976d2' }} />
            </Box>
        );
    }

    return (
        <Card
            variant="outlined"
            sx={{
                m: 2,
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                height: '80vh',
                overflow: 'hidden'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    backgroundColor: '#1976d2',
                    color: 'white'
                }}
            >
                <Tooltip title="Voltar" arrow>
                    <IconButton onClick={handleBack} sx={{ color: 'white', mr: 1 }}>
                        <ArrowBack />
                    </IconButton>
                </Tooltip>
                <Avatar sx={{ width: 56, height: 56, mr: 2 }}>
                    {patientName?.[0]?.toUpperCase()}
                </Avatar>
                <Typography variant="h2" fontWeight="bold" sx={{ color: '#f0f0f0' }}>
                    {patientName || t('messages.patient')}
                </Typography>
            </Box>

            <Divider />

            <Box
                sx={{
                    flex: 1,
                    p: 2,
                    overflowY: 'auto',
                    backgroundColor: '#f5f6fa'
                }}
            >
                {[...(messages || [])].reverse().map((msg) => {
                    const isMine = msg.sender?.uuid === roleUUID;

                    return (
                        <Box
                            key={msg.uuid}
                            display="flex"
                            justifyContent={isMine ? 'flex-end' : 'flex-start'}
                            mb={1.5}
                        >
                            <Paper
                                elevation={1}
                                sx={{
                                    padding: '12px 16px',
                                    maxWidth: '95%',
                                    backgroundColor: isMine ? '#1976d2' : '#e0e0e0',
                                    color: isMine ? 'white' : 'black',
                                    borderRadius: '16px'
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{ color: isMine ? 'white' : 'black' }}
                                >
                                    {msg.message}
                                </Typography>

                                <Box
                                    sx={{
                                        mt: 0.5,
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        alignItems: 'center',
                                        gap: 0.5
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            opacity: 0.8,
                                            color: isMine ? 'white' : 'black'
                                        }}
                                    >
                                        {formatMessageDate(msg.sent_at)}
                                    </Typography>

                                    {isMine &&
                                        (msg.seen ? (
                                            <CheckCircle
                                                fontSize="12px"
                                                sx={{ color: '#4caf50', opacity: 0.9 }}
                                            />
                                        ) : (
                                            <RadioButtonUnchecked
                                                fontSize="12px"
                                                sx={{ color: '#9e9e9e', opacity: 0.7 }}
                                            />
                                        ))}
                                </Box>
                            </Paper>
                        </Box>
                    );
                })}
                <div ref={bottomRef} />
            </Box>

            <Divider />

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    backgroundColor: '#fff'
                }}
            >
                <TextField
                    fullWidth
                    size="small"
                    placeholder={t('messages.placeholder')}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                />
                <IconButton
                    color="primary"
                    sx={{ ml: 1 }}
                    onClick={handleSend}
                    disabled={!newMessage.trim()}
                >
                    <Send />
                </IconButton>
            </Box>
        </Card>
    );
};

export default MessageChat;
